import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
 

const services = [
  {
    icon: 'fa-solid fa-headset',
    title: '1. Customer Support & Care',
    desc: 'Deliver exceptional 24/7 multi-channel support across live chat, email, phone, and social media to elevate customer satisfaction and retention.',
  },
  {
    icon: 'fa-solid fa-file-invoice-dollar',
    title: '2. Finance & Accounting BPO',
    desc: 'Streamline your financial operations with expert accounts payable/receivable, payroll processing, bookkeeping, and tax compliance services.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '3. Data Entry & Processing Services',
    desc: 'Ensure high data accuracy and operational efficiency with large-scale data digitization, cleansing, indexing, and management solutions.',
  },
  {
    icon: 'fa-solid fa-users-gear',
    title: '4. Human Resources & Recruitment Outsourcing',
    desc: 'Delegate talent acquisition, employee onboarding, HR administration, and performance tracking to scale your workforce seamlessly.',
  },
  {
    icon: 'fa-solid fa-cart-flatbed',
    title: '5. Supply Chain & Back-Office Support',
    desc: 'Optimize order processing, inventory tracking, vendor management, and logistics administration for frictionless operational workflows.',
  },
  {
    icon: 'fa-solid fa-bullhorn',
    title: '6. Telemarketing & Lead Generation',
    desc: 'Accelerate sales pipeline growth with targeted outbound calling, prospect qualification, appointment setting, and market research campaigns.',
  },
  {
    icon: 'fa-solid fa-shield-check',
    title: '7. Content Moderation & Quality Assurance',
    desc: 'Protect your brand identity and online community with round-the-clock moderation of user-generated content, images, and video media.',
  },
  {
    icon: 'fa-solid fa-notes-medical',
    title: '8. Technical Support & Help Desk Services',
    desc: 'Provide Tier 1 to Tier 3 technical support, software troubleshooting, and hardware issue resolution for your customers and employees.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: '9. Knowledge Process Outsourcing (KPO)',
    desc: 'Access advanced domain expertise including business analytics, market research, IP research, and financial modeling.',
  },
  {
    icon: 'fa-solid fa-handshake-angle',
    title: '10. Claims & Billing Processing',
    desc: 'Fast-track claim verification, medical or insurance billing, and payment processing with high accuracy and regulatory compliance.',
  },
];

const whyChooseUs = [
  'Cost reduction up to 60% on operational expenditure',
  '24/7/365 uninterrupted global service delivery',
  'Strict SLA management & KPI tracking',
  'ISO & GDPR compliant data security protocols',
  'Scalable teams that expand on demand',
  'Multi-lingual support capabilities',
  'Advanced AI-assisted workflow automation',
  'Rigorous quality control and continuous monitoring',
  'Smooth onboarding & knowledge transition',
  'Dedicated account management and reporting',
];

const processSteps = [
  { step: '01', title: 'Process Audit', desc: 'Analyze existing operational workflows, resource bottlenecks, and key performance indicators.' },
  { step: '02', title: 'SLA & Strategy Design', desc: 'Define target metrics, quality benchmarks, compliance protocols, and team structures.' },
  { step: '03', title: 'Knowledge Transfer', desc: 'Train dedicated teams on your brand guidelines, software platforms, and standard operating procedures.' },
  { step: '04', title: 'Pilot Execution', desc: 'Run controlled pilot campaigns to calibrate workflows, verify accuracy, and refine team efficiency.' },
  { step: '05', title: 'Full Scale Operations', desc: 'Transition full operational workload while establishing real-time tracking dashboards.' },
  { step: '06', title: 'Optimization & Growth', desc: 'Continuously assess performance metrics, automate routine tasks, and expand team capabilities.' },
];

const techStack = [
  { category: 'Customer Care Platforms', items: ['Zendesk', 'Salesforce', 'Freshdesk', 'HubSpot', 'Intercom'] },
  { category: 'ERP & Back-Office', items: ['SAP', 'Oracle NetSuite', 'QuickBooks', 'Xero', 'Microsoft Dynamics'] },
  { category: 'Communication & VOIP', items: ['Twilio', 'RingCentral', 'Avaya', 'Slack', 'Microsoft Teams'] },
  { category: 'Analytics & Automation', items: ['Power BI', 'UiPath', 'Zapier', 'Tableau', 'Excel Automation'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ProcessOutsourcing = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Business Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Process <span className="gradient-text-cyan">Outsourcing</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we empower organizations to scale rapidly, cut operating costs, and optimize back-office and front-line operations with tailored Business Process Outsourcing (BPO) solutions.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Outsource With Lidlbritain &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              BPO Services by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Outsource to <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Platform Ecosystem */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Platforms & Tools We Utilize</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Outsourcing Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Scale Operations Effortlessly with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Hand over repetitive operational burdens to Lidlbritain’s specialized BPO teams so you can focus entirely on strategic growth.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Get a Process Outsourcing Quote &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProcessOutsourcing;